/**
 * L'agenda du jour.
 *
 * Le patron veut un tableau de bord qui montre « tout ce qu'une secrétaire
 * montrerait à son patron pour piloter sa journée ». Elle commence par
 * l'heure : « à 9 h le métré chez Durand, à 14 h la réception du ravalement ».
 *
 * Deux choix, pour la saisie comme pour la lecture :
 *
 *   · **le prochain rendez-vous est marqué**, et ceux qui sont passés
 *     s'effacent sans disparaître. À 11 h, le métré de 9 h n'est plus une
 *     information, mais on doit pouvoir vérifier qu'il a eu lieu ;
 *   · **l'intitulé se déduit s'il est laissé vide** — « Métré — Durand ». Un
 *     rendez-vous se note au téléphone, le combiné à l'épaule : chaque champ
 *     obligatoire en trop est un rendez-vous qui finit sur un post-it.
 */
import { useMemo, useState } from "react";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import type { Appointment, AppointmentKind } from "@dker/core";
import {
  APPOINTMENT_KIND_LABELS,
  appointmentMoment,
  clientDisplayName,
  nextAppointment,
} from "@dker/core";
import { Button } from "@/components/forms/Button";
import { Field } from "@/components/forms/Field";
import { Input } from "@/components/forms/Input";
import { Select } from "@/components/forms/Select";
import { Skeleton } from "@/components/shared/Skeleton";
import { Panel } from "@/components/shared/Panel";
import { useAgendaOfDay, useRemoveAppointment, useSaveAppointment } from "@/hooks/useAgenda";
import { useClients } from "@/hooks/useClients";
import { useActiveProjects } from "@/hooks/useProjects";
import { cn } from "@/lib/utils";
import { Dialog, DialogCloseButton } from "@/components/shared/Dialog";

const heure = (iso: string) =>
  new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

export function AgendaDuJour({ compact = false }: { compact?: boolean }) {
  const { data, isLoading } = useAgendaOfDay();
  const retirer = useRemoveAppointment();
  const [edition, setEdition] = useState<Appointment | "new" | null>(null);

  const rdv = data ?? [];
  const maintenant = new Date();
  const prochain = nextAppointment(rdv, maintenant);

  return (
    <Panel
      title="Agenda du jour"
      icon={CalendarDays}
      actions={
        /* Sur le plus petit téléphone, le « + » seul : le libellé faisait
           passer le bouton sur deux lignes. */
        <Button
          size="sm"
          onClick={() => setEdition("new")}
          aria-label="Nouveau rendez-vous"
          className="px-2 min-[360px]:px-3"
        >
          <Plus size={14} />
          <span className="hidden min-[360px]:inline">Rendez-vous</span>
        </Button>
      }
    >

      {isLoading ? (
        <Skeleton className="h-24" />
      ) : rdv.length === 0 ? (
        /* Affiché vide, comme la file : « rien aujourd'hui » répond à la
           question, un panneau absent la laisserait ouverte. */
        <p className="text-xs text-text-muted text-center py-6">
          Aucun rendez-vous aujourd’hui.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {(compact ? rdv.filter((r) => appointmentMoment(r, maintenant) !== "passe") : rdv).map(
            (r) => {
              const moment = appointmentMoment(r, maintenant);
              const estProchain = prochain?.id === r.id;
              const qui = [r.clientName, r.projectName].filter(Boolean).join(" · ");
              return (
                <li
                  key={r.id}
                  className={cn(
                    "flex items-start gap-3 px-4 py-3 border-l-[3px]",
                    estProchain ? "border-accent" : "border-transparent",
                    moment === "passe" && "opacity-55",
                  )}
                >
                  <span className="font-mono text-[15px] text-text-primary w-12 shrink-0 tabular-nums">
                    {heure(r.startsAt)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEdition(r)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="text-sm text-text-primary">
                      {r.title}
                      {moment === "en_cours" && (
                        <span className="ml-2 text-[10.5px] uppercase tracking-[0.12em] text-accent-ink">
                          en cours
                        </span>
                      )}
                    </p>
                    {(qui || r.location) && (
                      <p className="text-[12.5px] text-text-muted mt-0.5">
                        {[qui, r.location].filter(Boolean).join(" — ")}
                      </p>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => retirer.mutate(r.id)}
                    disabled={retirer.isPending}
                    className="p-1.5 text-text-muted hover:text-danger-ink shrink-0"
                    aria-label={`Supprimer ${r.title}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            },
          )}
          {compact && prochain == null && rdv.length > 0 && (
            <li className="px-4 py-3 text-xs text-text-muted">
              Les rendez-vous du jour sont passés.
            </li>
          )}
        </ul>
      )}

      {edition && (
        <AppointmentDialog
          appointment={edition === "new" ? null : edition}
          onClose={() => setEdition(null)}
        />
      )}
    </Panel>
  );
}

const DUREES = [
  { value: "", label: "Non précisée" },
  { value: "30", label: "30 min" },
  { value: "60", label: "1 h" },
  { value: "120", label: "2 h" },
  { value: "240", label: "Demi-journée" },
];

/** « 2026-09-14 » et « 09:30 » depuis un instant, en heure locale. */
function localParts(iso: string | null): { date: string; time: string } {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: iso ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : "",
  };
}

export function AppointmentDialog({
  appointment,
  onClose,
}: {
  appointment: Appointment | null;
  onClose: () => void;
}) {
  const save = useSaveAppointment();
  const { data: clients } = useClients();
  const { data: chantiers } = useActiveProjects();

  const depart = localParts(appointment?.startsAt ?? null);
  const [kind, setKind] = useState<AppointmentKind>(appointment?.kind ?? "visite");
  const [titre, setTitre] = useState(appointment?.title ?? "");
  const [date, setDate] = useState(depart.date);
  const [time, setTime] = useState(depart.time);
  const [duree, setDuree] = useState(appointment?.durationMinutes?.toString() ?? "");
  const [clientId, setClientId] = useState(appointment?.clientId ?? "");
  const [projectId, setProjectId] = useState(appointment?.projectId ?? "");
  const [lieu, setLieu] = useState(appointment?.location ?? "");

  const client = (clients ?? []).find((c) => c.id === clientId) ?? null;
  const chantier = (chantiers ?? []).find((p) => p.id === projectId) ?? null;

  // L'intitulé proposé quand le champ est vide : la nature et à qui. C'est ce
  // que le patron aurait écrit, et c'est ce qu'il n'a pas le temps d'écrire.
  const titreDeduit = useMemo(() => {
    const qui = client ? clientDisplayName(client) : (chantier?.name ?? "");
    return `${APPOINTMENT_KIND_LABELS[kind]}${qui ? ` — ${qui}` : ""}`;
  }, [kind, client, chantier]);

  const incomplet = !date || !time;

  return (
    <Dialog
      onClose={onClose}
      labelledBy="appointment-title"
      className="max-h-[92vh] overflow-y-auto"
      dismissible={!save.isPending}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <h2 id="appointment-title" className="text-lg font-semibold text-text-primary">
          {appointment ? "Rendez-vous" : "Nouveau rendez-vous"}
        </h2>
        <DialogCloseButton disabled={save.isPending} />
      </div>

      <div className="space-y-4">
        {/* Trois champs de front ne tiennent pas sous 400 px : la date et
            l'heure se coupaient. La durée passe alors dessous. */}
        <div className="grid grid-cols-2 min-[400px]:grid-cols-3 gap-3">
          <Field label="Jour" required>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Heure" required>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} autoFocus />
          </Field>
          <Field label="Durée" className="col-span-2 min-[400px]:col-span-1">
            <Select value={duree} onChange={(e) => setDuree(e.target.value)}>
              {DUREES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Nature">
          <Select value={kind} onChange={(e) => setKind(e.target.value as AppointmentKind)}>
            {(Object.keys(APPOINTMENT_KIND_LABELS) as AppointmentKind[]).map((k) => (
              <option key={k} value={k}>
                {APPOINTMENT_KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Client">
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">—</option>
              {(clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {clientDisplayName(c)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Chantier">
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">—</option>
              {(chantiers ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Intitulé" hint={titre.trim() ? undefined : `Laissé vide : « ${titreDeduit} ».`}>
          <Input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder={titreDeduit} />
        </Field>

        <Field label="Lieu" hint="Facultatif.">
          <Input
            value={lieu}
            onChange={(e) => setLieu(e.target.value)}
            placeholder={
              [client?.addressLine1, client?.city].filter(Boolean).join(", ") ||
              [chantier?.siteAddress, chantier?.siteCity].filter(Boolean).join(", ") ||
              "12 rue des Pins, Lyon"
            }
          />
        </Field>
      </div>

      <div className="flex gap-2 mt-6">
        <Button variant="secondary" className="flex-1" onClick={onClose} disabled={save.isPending}>
          Annuler
        </Button>
        <Button
          className="flex-1"
          disabled={incomplet}
          loading={save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: appointment?.id,
                // Heure locale : c'est l'heure que le patron a tapée, et
                // `new Date(jour + heure)` sans fuseau la lit comme telle.
                startsAt: new Date(`${date}T${time}`).toISOString(),
                durationMinutes: duree === "" ? null : Number(duree),
                kind,
                title: titre.trim() || titreDeduit,
                clientId: clientId || null,
                projectId: projectId || null,
                location: lieu.trim() || null,
              },
              { onSuccess: onClose },
            )
          }
        >
          Enregistrer
        </Button>
      </div>
    </Dialog>
  );
}
